import React from 'react';
import clsx from 'clsx';
import { Autocomplete } from '@material-ui/lab';
import { Button, createStyles, FormHelperText, Grid, Icon, IconButton, makeStyles, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@material-ui/core';

import { AuthContext } from '../../../Authentication/AuthContext';
import { ClientClipperFilterModel, clientClipperFilterModelInitial } from '../../../../Models/ClientClipperFilterModel';
import { clippingParameterModelInitial, ClippingParameterModel } from '../../../../Models/ClippingParameterModel';
import { theme } from '../../../../theme';
import { EditAction } from '../../../../shared/enum/EditAction';
import { ClientClipperContext } from '../ClientClipperContext';
import { ClippingParametersContext } from '../ClippingParametersContext';

const useStyles = makeStyles(() => createStyles({
    clippingParametersSection: {
        marginTop: theme.spacing(2)
    },
    paper: {
        padding: theme.spacing(3),
    },
    paperError: {
        border: `1px solid ${theme.palette.error.main}`
    }
}));

export function ModalClippingParameters() {
    const classes = useStyles();

    const clientClipperContext = React.useContext(ClientClipperContext);
    const clippingParametersContext = React.useContext(ClippingParametersContext);
    const authContext = React.useContext(AuthContext);
    
    const [clippingParameterNotSelectedError, setClippingParameterNotSelectedError] = React.useState<boolean>(false);
    const [clippingParameterValueNotDefinedError, setClippingParameterValueNotDefinedError] = React.useState<boolean>(false);

    const handleClippingParameterTableEdit = (clipperFilter: ClientClipperFilterModel, index: number) => {
        clippingParametersContext.setClippingParametersEditMode(true);
        clippingParametersContext.setCurrentClippingParameter(clipperFilter.clippingParameter);
        clippingParametersContext.setCurrentClientClipperFilter(clipperFilter);
        clippingParametersContext.setCurrentClippingParameterIndex(index);        
    };

    const handleClippingParametersTableDelete = (index: number) => {
        const clippingFilters= JSON.parse(JSON.stringify(clientClipperContext.currentClientClipper.clippingFilters));

        if (clippingFilters[index].editAction == EditAction.INSERT)
            clippingFilters.splice(index, 1);
        else
            clippingFilters[index].editAction = EditAction.DELETE;
            
        clientClipperContext.setCurrentClientClipper({...clientClipperContext.currentClientClipper, clippingFilters: [...clippingFilters]});
        clippingParametersContext.setCurrentClippingParameterIndex(-1);
    };

    const hasClippingParameterError = () => {
        const errorClippingParameterNotSelected = !clippingParametersContext.currentClippingParameter || !clippingParametersContext.currentClippingParameter.id || clippingParametersContext.currentClippingParameter.id < 1;
        const errorValueNotDefined = !clippingParametersContext.currentClientClipperFilter.clippingParameterValue;

        setClippingParameterNotSelectedError(errorClippingParameterNotSelected);
        setClippingParameterValueNotDefinedError(errorValueNotDefined);

        return errorClippingParameterNotSelected || errorValueNotDefined;
    };
    
    const clearClippingParametersFields = () => {
        clippingParametersContext.setCurrentClippingParameter(clippingParameterModelInitial);
        clippingParametersContext.setCurrentClientClipperFilter(clientClipperFilterModelInitial);
        clippingParametersContext.setCurrentClippingParameterIndex(-1);
        clippingParametersContext.setClippingParametersEditMode(false);
        setClippingParameterNotSelectedError(false);
        setClippingParameterValueNotDefinedError(false);
    };

    const handleSaveClippingParameter = () => {
        if (!hasClippingParameterError()) {
            const currentFilter = JSON.parse(JSON.stringify(clippingParametersContext.currentClientClipperFilter));
            currentFilter.clippingParameter = clippingParametersContext.currentClippingParameter;
            currentFilter.userId = authContext.login.user.id;

            const clippingFilters = clientClipperContext.currentClientClipper.clippingFilters
                ? JSON.parse(JSON.stringify(clientClipperContext.currentClientClipper.clippingFilters))
                : [];

            if (clippingParametersContext.clippingParametersEditMode) {
                clippingFilters[clippingParametersContext.currentClippingParameterIndex] = currentFilter;

                if (clippingFilters[clippingParametersContext.currentClippingParameterIndex].editAction != EditAction.INSERT)
                    clippingFilters[clippingParametersContext.currentClippingParameterIndex].editAction = EditAction.UPDATE;

                clippingParametersContext.setClippingParametersEditMode(false);
            }
            else
                clippingFilters.push({ ...currentFilter, editAction: EditAction.INSERT });

            clientClipperContext.setCurrentClientClipper({...clientClipperContext.currentClientClipper, clippingFilters: [...clippingFilters]});
            
            clearClippingParametersFields();
        }
    };
    
    const handleClippingParameterChange = (parameter: ClippingParameterModel | null) => {
        if (parameter)
            clippingParametersContext.setCurrentClippingParameter({ ...parameter});
        else
            clippingParametersContext.setCurrentClippingParameter(clippingParameterModelInitial);        
    };

    const getClippingParametersOptions = () => {
        return (
            <Grid item xs={6}>
                <Autocomplete
                    id="clipping-parameters-options"
                    options={clientClipperContext.currentClipper
                        ? (clientClipperContext.currentClipper?.clippingParameters
                            && clientClipperContext.currentClipper?.clippingParameters.filter(cp =>
                                clientClipperContext.currentClientClipper.clippingFilters.filter(cf =>
                                    cf.clippingParameter?.id == cp.id && cf.editAction != EditAction.DELETE).length == 0))
                        : []}
                    value={clippingParametersContext.currentClippingParameter}
                    onChange={(_, newValue: ClippingParameterModel | null) => { handleClippingParameterChange(newValue); }}
                    getOptionLabel={(option) => option.description}
                    disabled={clippingParametersContext.clippingParametersEditMode}
                    fullWidth
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField
                            {...params}
                            label="Filtro"
                            variant="outlined"
                            error={clippingParameterNotSelectedError && (!clippingParametersContext.currentClippingParameter || clippingParametersContext.currentClippingParameter.id < 1)}
                            helperText={clippingParameterNotSelectedError && (!clippingParametersContext.currentClippingParameter || clippingParametersContext.currentClippingParameter.id < 1)
                                ? 'Campo obrigatório'
                                : ''} />
                    } />
            </Grid>
        );
    };

    const getClippingParametersValue = () => {
        return(
            <Grid item xs={6}>
                <TextField
                    id="clipping-parameters-value"
                    value={clippingParametersContext.currentClientClipperFilter.clippingParameterValue}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        clippingParametersContext.setCurrentClientClipperFilter({...clippingParametersContext.currentClientClipperFilter, clippingParameterValue: event.target.value})}
                    fullWidth
                    label="Valor"
                    variant="outlined"
                    error={clippingParameterValueNotDefinedError && !clippingParametersContext.currentClientClipperFilter.clippingParameterValue}
                    helperText={clippingParameterNotSelectedError && !clippingParametersContext.currentClientClipperFilter.clippingParameterValue
                        ? 'Campo obrigatório'
                        : ''} />
            </Grid>
        );
    };

    const getClippingParametersAddNewButton = () => {
        return (
            <Grid item xs={12}>
                <Grid 
                    container 
                    justify="flex-end"
                    alignItems="flex-end">
                    <Grid item>
                        <Button 
                            variant="contained" 
                            color='primary'
                            onClick={handleSaveClippingParameter} 
                            size='large'>
                            {clippingParametersContext.clippingParametersEditMode ? 'Salvar Filtro' : 'Adicionar Filtro'}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const getClippingParametersList = () => {
        return (
            <>
                { clientClipperContext.currentClientClipper.clippingFilters &&  
                clientClipperContext.currentClientClipper.clippingFilters.length > 0 && 
                clientClipperContext.currentClientClipper.clippingFilters.find(c => c.editAction != EditAction.DELETE) &&
                <Grid item xs={12}>
                    <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">Descrição</TableCell>
                                    <TableCell align="left">Valor</TableCell>
                                    <TableCell align="center">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clientClipperContext.currentClientClipper.clippingFilters.map((clippingFilters, index) => (
                                    clippingFilters.editAction != EditAction.DELETE && (
                                        <TableRow key={index}>
                                            <TableCell align="left">
                                                {clippingFilters.clippingParameter?.description}
                                            </TableCell>
                                            <TableCell align="left">
                                                {clippingFilters.clippingParameterValue}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { handleClippingParameterTableEdit(clippingFilters, index); }}>
                                                    <Icon>edit</Icon>
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { handleClippingParametersTableDelete(index); }}>
                                                    <Icon>delete_outline</Icon>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid> }
            </>);
    };

    return (
        <Grid item>
            <Paper
                elevation={3}
                className={clsx(classes.paper ,{
                    [classes.paperError]: clippingParametersContext.requiredClippingParameterError &&
                        !(clientClipperContext.currentClientClipper.clippingFilters &&
                            clientClipperContext.currentClientClipper.clippingFilters.filter(cf => cf.editAction != EditAction.DELETE).length > 0)})}>
                <Grid
                    container
                    direction="row"
                    spacing={2}
                    className={classes.clippingParametersSection}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1">Filtros clipping automático</Typography>
                    </Grid>
                    { getClippingParametersOptions() }
                    { getClippingParametersValue() }
                    { getClippingParametersAddNewButton() }
                    { getClippingParametersList() }
                </Grid>
            </Paper>
            { (clippingParametersContext.requiredClippingParameterError &&
                !(clientClipperContext.currentClientClipper.clippingFilters &&
                    clientClipperContext.currentClientClipper.clippingFilters.filter((cf) => cf.editAction != EditAction.DELETE).length > 0)) && (
                <FormHelperText error>
                    Você precisa inserir pelo menos um filtro de clipping automático esta clipadora.
                </FormHelperText>)
            }
        </Grid>
    );
}